// Боковое меню (п.20, финал переезда 2026-09-07): проект, разделы, выход,
// сворачивание. Состав и порядок пунктов — как в прежней оболочке.
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { alertBox, ask, confirmBox } from '../dialog';
import { call, type Data } from '../api';
import { ProektNastrojki } from '../ProektNastrojki';
import { at as yakorPop, type Anchor } from '../Pop';
import { parseDate } from '../util';

type Me = Data<'/api/auth/me', 'get'>;
type Project = Data<'/api/projects', 'get'>[number];

export type TabId =
  | 'obshchee' | 'plan' | 'ruch' | 'struktura' | 'checks' | 'struct' | 'works' | 'podryad' | 'shemy' | 'links' | 'spiski'
  | 'resurs' | 'kvart' | 'fronty' | 'fakt' | 'faktlog' | 'rep' | 'users' | 'wiki';

type Item = { id: TabId; label: string; title?: string; hidden?: boolean };
/** ссылка на другое приложение (сайт шахматки) — не вкладка, открывается рядом */
type Vneshnyaya = { label: string; url: string; title?: string };
type Group = { key: string; label: string; items: Item[]; sub?: { key: string; label: string; items: Item[] };
               admin?: boolean; vneshnie?: Vneshnyaya[]; verh?: boolean };

export const GROUPS: Group[] = [
  // три способа планирования — на самом верху, без заголовка (владелец 2026-10-16). Первый пункт,
  // «Типовое планирование», рисуется отдельно: это экран с четырьмя вкладками сверху (см. App, VKLADKI)
  { key: 'grpSposob', label: '', verh: true, items: [
      // чертежи и захватки (ТЗ 2026-09-19 «Графическое планирование»; до 2026-10-16 — «Схемы» в «Справочниках»)
      { id: 'shemy', label: 'Графическое планирование', title: 'Чертежи проекта: план паркинга и этажей, на которых размечаются захватки' },
      // модель ГПР в режиме ручного ввода: задачи работ с раскаткой «вручную»
      { id: 'ruch', label: 'Ручное планирование', title: 'Задачи, которые ставятся вручную: корпус, этаж, работа, длительность, предшественник' },
    ] },
  { key: 'grpSprav', label: 'Справочники', items: [
      { id: 'struct', label: 'Этажи' },
      { id: 'works', label: 'Работы' },
      // справочник подрядчиков проекта (решение владельца 2026-09-14); в «Глобально» не показывается
      { id: 'podryad', label: 'Подрядчики', title: 'Подрядчики проекта: контакты, на каких корпусах какие работы ведут, привязанные пользователи' },
      // «Связи» (владелец 2026-10-05; с 2026-09-06 называлось «Предшественники»)
      { id: 'links', label: 'Связи', title: 'Схема связей между работами проекта: та же информация, что теги «Предшественники» в «Работах»' },
      { id: 'spiski', label: 'Списки' },
    ], sub: { key: 'grpDop', label: 'Доп. справочники', items: [
      { id: 'resurs', label: 'Ресурсы' }, { id: 'kvart', label: 'Квартиры' },
    ] } },
  { key: 'grpPlan', label: 'Планирование', items: [
      // «Общее» (владелец 2026-09-15, #96): старт проекта и сводка по этапам
      { id: 'obshchee', label: 'Общее', title: 'Старт проекта и сводка по этапам работ: начало, окончание' },
      { id: 'plan', label: 'Модель ГПР' },
      // «Структура работ» — иерархия отдельным экраном (владелец 2026-10-15: «иерархия — отдельная
      // сущность»); открывается верхней вкладкой «Работы», в левом меню не показывается
      { id: 'struktura', label: 'Структура работ', hidden: true,
        title: 'Этапы и работы проекта: набираются прямо в таблице, связи — на схеме справа' },
      { id: 'checks', label: 'Проверки' },
    ] },
  { key: 'grpFakt', label: 'Факт', items: [
      { id: 'fronty', label: 'Открытые фронты', title: 'Что можно брать в работу сейчас: предшественники закончены по факту' },
      { id: 'fakt', label: 'Шахматка факта' },
      { id: 'faktlog', label: 'Журнал' },
      // «Отчёты» скрыты 2026-09-06 (решение владельца), экран цел
      { id: 'rep', label: 'Отчёты', hidden: true },
    ],
    // сайт шахматки подрядчиков — отдельное приложение на /fakt/ (2026-09-10):
    // открывается рядом, вход тот же, планер видит ровно то, что видят они
    vneshnie: [{ label: 'Шахматка подрядчиков ↗', url: '/fakt/',
                 title: 'Открыть сайт шахматки подрядчиков в новой вкладке: то же, что видят они. '
                      + 'Вход тот же — заново входить не нужно' }] },
  { key: 'grpAdmin', label: 'Администрирование', admin: true, items: [{ id: 'users', label: 'Пользователи' }] },
  { key: 'grpWiki', label: 'Справка', items: [{ id: 'wiki', label: 'Как считаются сроки' }] },
];
/** что доступно в проекте «Глобально» (общие справочники) */
const GLOBAL_TABS: TabId[] = ['struktura', 'works', 'links', 'spiski', 'users', 'wiki'];
export const TABS: Item[] = GROUPS.flatMap(g => g.items.concat(g.sub?.items || []));

export function Menu({ tab, onTab, me, role, isGlobal, projects, pid, onProject, onNewProject, onLogout, onLogoutAll, tipovoe, onTipovoe, onProektIzmenen }: {
  tab: TabId; onTab: (t: TabId) => void; me: Me; role: string | null; isGlobal: boolean;
  /** экраны «Типового планирования» (четыре вкладки сверху) и переход туда — на последнюю открытую */
  tipovoe: TabId[]; onTipovoe: () => void;
  projects: Project[]; pid: number | null;
  onProject: (id: number) => void; onNewProject: (id: number) => void; onLogout: () => void;
  /** «Настройки проекта» сохранены или проект удалён (msg, удалён ли) — перечитать список проектов */
  onProektIzmenen?: (msg: string, udalen: boolean) => void;
  onLogoutAll: () => void;
}) {
  const [nastr, setNastr] = useState<Anchor | null>(null);   // окно «Настройки проекта»
  const [mini, setMini] = useState(() => {
    try { return localStorage.getItem('plot_side') === '1'; } catch { return false; }
  });
  function toggle() {
    setMini(v => {
      const next = !v;
      try { localStorage.setItem('plot_side', next ? '1' : ''); } catch { /* приватный режим */ }
      document.getElementById('appView')?.classList.toggle('mini', next);
      return next;
    });
  }
  // класс на слое отвечает за узкое меню (стили оболочки)
  document.getElementById('appView')?.classList.toggle('mini', mini);

  async function newProject() {
    const name = (await ask('Название нового проекта:') || '').trim();
    if (!name) return;
    // библиотека-шаблон (#7): работы, связи и списки из «Глобально»
    const iz = await confirmBox('Взять работы, связи и списки из «Глобально»?',
      { podpis: 'Нет — проект будет пустым, работы можно добавить позже через «Справочники» на вкладке «Работы».',
        ok: 'Взять', cancel: 'Нет, пустой проект' });
    // старт — сразу при создании (замер кликов, 2026-10-18: раньше отдельным походом в ⚙)
    let start: string | null | undefined = null;
    for (;;) {
      const t = await ask('Дата старта проекта (ДД.ММ.ГГГГ):', '', { podpis: 'Раньше этой даты не начнётся ни одна задача. Можно оставить пустым и задать потом в ⚙.', ok: 'Готово' });
      if (!t) { start = null; break; }
      start = parseDate(t);
      if (start) break;
      await alertBox('Дата не разобрана: нужно ДД.ММ.ГГГГ, например 01.03.2027');
    }
    const r = await call<{ id: number }>('post', '/api/projects', { name, iz_globalno: iz });
    if (!r.ok) { await alertBox(r.error.message); return; }
    if (start) {
      const rs = await call('patch', '/api/projects/' + r.data.id, { data_starta: start });
      if (!rs.ok) await alertBox('Проект создан, но старт не сохранился: ' + rs.error.message + '. Задайте его в ⚙.');
    }
    try { localStorage.setItem('plot_pid', String(r.data.id)); } catch { /* приватный режим */ }
    onNewProject(r.data.id);
  }

  const visible = (i: Item) => !i.hidden && (!isGlobal || GLOBAL_TABS.includes(i.id));
  const link = (i: Item) => (
    <a key={i.id} className={tab === i.id ? 'on' : ''} title={i.title}
      onClick={() => onTab(i.id)}>
      {isGlobal && i.id === 'links' ? 'Шаблоны связей' : i.label}
    </a>
  );

  return (
    <aside className="side">
      <div className="side-top">
        <div className="brand">КАСКАД</div>
        <a className="side-tgl" title="Свернуть/развернуть меню" onClick={toggle}>{mini ? '»' : '«'}</a>
      </div>
      <div className="who" title={me.org ? 'Организация: ' + me.org : undefined}>
        {me.name + (me.org ? ' · ' + me.org : '') + (role ? ' · ' + role : '')}</div>
      <div className="proj">
        <select value={pid ?? ''} title="Проект" onChange={e => onProject(+e.target.value)}>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {/* «Настройки проекта» (2026-10-17): название, старт, праздники, удаление */}
        {pid != null && <button className="sec proj-nastr" title="Настройки проекта: название, старт, праздники, удаление"
          onClick={e => setNastr(yakorPop(e))}>⚙</button>}
        {me.is_admin && <button title="Создать новый проект (администратор)" onClick={() => void newProject()}>＋</button>}
      </div>
      {nastr && (() => {
        const pr = projects.find(x => x.id === pid);
        if (!pr) return null;
        const pravit = role === 'администратор' || role === 'планер';
        // окно — на уровне страницы: внутри левой панели оно обрезалось её краем (2026-10-17)
        return createPortal(<ProektNastrojki proekt={pr} at={nastr} mozhnoPravit={pravit} mozhnoUdalit={role === 'администратор'}
          onClose={() => setNastr(null)}
          onIzmenen={m => { setNastr(null); onProektIzmenen?.(m, false); }}
          onUdalen={m => { setNastr(null); onProektIzmenen?.(m, true); }} />, document.body);
      })()}
      {GROUPS.map(g => {
        if (g.admin && !me.is_admin) return null;
        const items = g.items.filter(visible);
        const sub = g.sub ? g.sub.items.filter(visible) : [];
        // ссылки на соседние приложения в проекте «Глобально» не нужны: там нет факта
        const vnesh = isGlobal ? [] : (g.vneshnie || []);
        if (g.verh) {
          // «Глобально»: вместо «Типового планирования» — «Структура работ» (вкладка «Работы», 2026-10-17)
          if (isGlobal) return (
            <div key={g.key} className="side-verh">
              <a className={tab === 'struktura' ? 'on' : ''} onClick={() => onTab('struktura')}
                title="Структура работ справочника: этапы и работы, как на вкладке «Работы» в проекте">Структура работ</a>
            </div>);
          return (
            <div key={g.key} className="side-verh">
              {/* «Ручное» живёт на вкладке «Планирование», но в меню у него свой пункт — не подсвечиваем оба (#125) */}
              <a className={tipovoe.includes(tab) && tab !== 'ruch' ? 'on' : ''} onClick={onTipovoe}
                title="Этажи, работы, планирование, отчёты — четыре вкладки сверху">Типовое планирование</a>
              {items.map(link)}
            </div>);
        }
        if (!items.length && !sub.length && !vnesh.length) return null;
        return (
          <details key={g.key} open>
            <summary className="sec-h">{g.label}</summary>
            {items.map(link)}
            {g.sub && sub.length > 0 && (
              <details key={g.sub.key}>
                <summary className="sec-h">{g.sub.label}</summary>
                {sub.map(link)}
              </details>
            )}
            {vnesh.map(v => (
              <a key={v.url} href={v.url} target="_blank" rel="noopener noreferrer" title={v.title}>
                {v.label}
              </a>
            ))}
          </details>
        );
      })}
      <div className="side-foot">
        <a onClick={onLogout}>Выйти</a>
        {!mini && <a onClick={onLogoutAll} style={{ fontSize: 12, opacity: .7 }}
          title="Погасить все свои входы, кроме этого — если ссылка с доступом куда-то попала">
          Выйти везде</a>}
      </div>
    </aside>
  );
}
